import { Edit3, Layers, Receipt, Trash2, User } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useWatch } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import { CurrencyInput } from '@/shared/components/inputs';
import { DetailCard } from '@/shared/components/layout';
import {
  Badge,
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogFooter,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
  Textarea,
  Toggle,
} from '@/shared/components/ui';
import { formatCurrencyFromCents } from '@/shared/utils/money';
import type {
  BillingPeriodStatus,
  ContractBillingPeriodFormData,
  ContractFormData,
} from '../types/contract-form';

export interface BillingContractSource {
  contractId: string;
  contractLabel: string;
  isRootContract: boolean;
  startDate: string;
  endDate: string;
  paymentTermsDays?: number;
  billingPeriods: ContractBillingPeriodFormData[];
}

type ContractBillingTabProps = {
  contractId: string;
  form: UseFormReturn<ContractFormData, any, ContractFormData>;
  fallbackBillingInterval?: ContractFormData['billingInterval'];
  readOnly?: boolean;
  subcontractSources?: BillingContractSource[];
  onSubcontractBillingPeriodsChange?: (
    contractId: string,
    billingPeriods: ContractBillingPeriodFormData[]
  ) => void;
};

type BillingRow = {
  contractId: string;
  contractLabel: string;
  isRootContract: boolean;
  period: ContractBillingPeriodFormData;
  periodIndex: number;
  sourceIndex: number;
};

type EditingBillingPeriodTarget = {
  contractId: string;
  periodIndex: number;
};

const isValidBillingInterval = (value: unknown): value is ContractFormData['billingInterval'] =>
  value === 'monthly' || value === 'quarterly' || value === 'yearly';

const getStatusBadgeClass = (status: BillingPeriodStatus, opts?: { overdue?: boolean }) => {
  if (opts?.overdue) {
    return 'bg-destructive/10 text-destructive border-destructive/20 hover:bg-destructive/15';
  }

  switch (status) {
    case 'scheduled':
      return 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80';
    case 'billable':
      return 'bg-warning-subtle text-warning-subtle-foreground border-warning-subtle hover:bg-warning-subtle/80';
    case 'invoiced':
      return 'bg-info-subtle text-info-subtle-foreground border-info-subtle hover:bg-info-subtle/80';
    case 'paid':
      return 'bg-success-subtle text-success-subtle-foreground border-success-subtle hover:bg-success-subtle/80';
    default:
      return 'bg-secondary text-secondary-foreground border-transparent hover:bg-secondary/80';
  }
};

const isOverdueBillingPeriod = (period: ContractBillingPeriodFormData) => {
  if (!period.dueDate || period.status === 'paid') return false;
  const due = DateTime.fromISO(period.dueDate);
  return due.isValid && due < DateTime.now();
};

export function ContractBillingTab({
  contractId,
  form,
  fallbackBillingInterval,
  readOnly = false,
  subcontractSources = [],
  onSubcontractBillingPeriodsChange,
}: ContractBillingTabProps) {
  const { t, locale } = useIntl();
  const watchedRootBillingPeriods =
    useWatch({ control: form.control, name: 'billingPeriods' }) || [];
  const watchedBillingInterval = useWatch({
    control: form.control,
    name: 'billingInterval',
  });

  const normalizedBillingInterval = isValidBillingInterval(watchedBillingInterval)
    ? watchedBillingInterval
    : fallbackBillingInterval || 'monthly';

  useEffect(() => {
    if (watchedBillingInterval !== normalizedBillingInterval) {
      form.setValue('billingInterval', normalizedBillingInterval, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [form, normalizedBillingInterval, watchedBillingInterval]);

  const rootBillingSource = useMemo<BillingContractSource>(
    () => ({
      contractId,
      contractLabel: t('contracts.rootContract'),
      isRootContract: true,
      startDate: form.getValues().startDate || '',
      endDate: form.getValues().endDate || '',
      paymentTermsDays: form.getValues().paymentTermsDays ?? 30,
      billingPeriods: watchedRootBillingPeriods,
    }),
    [contractId, form, t, watchedRootBillingPeriods]
  );

  const billingSources = useMemo(
    () => [rootBillingSource, ...subcontractSources],
    [rootBillingSource, subcontractSources]
  );
  const isAggregatedRootView =
    subcontractSources.length > 0 || Boolean(onSubcontractBillingPeriodsChange);
  const canEdit = !readOnly;

  const [addTargetContractId, setAddTargetContractId] = useState(contractId);
  const [showSubcontracts, setShowSubcontracts] = useState(true);
  const [editingTarget, setEditingTarget] = useState<EditingBillingPeriodTarget | null>(null);
  const [editingBillingPeriodSnapshot, setEditingBillingPeriodSnapshot] =
    useState<ContractBillingPeriodFormData | null>(null);

  useEffect(() => {
    const targetExists = billingSources.some((source) => source.contractId === addTargetContractId);
    if (!targetExists) {
      setAddTargetContractId(contractId);
    }
  }, [addTargetContractId, billingSources, contractId]);

  const mergedBillingRows = useMemo<BillingRow[]>(
    () =>
      billingSources
        .filter((source) => showSubcontracts || source.isRootContract)
        .flatMap((source, sourceIndex) =>
          source.billingPeriods.map((period, periodIndex) => ({
            sourceIndex,
            contractId: source.contractId,
            contractLabel: source.contractLabel,
            isRootContract: source.isRootContract,
            period,
            periodIndex,
          }))
        )
        .sort((left, right) => {
          const leftStart = DateTime.fromISO(left.period.startDate || '');
          const rightStart = DateTime.fromISO(right.period.startDate || '');
          const leftEnd = DateTime.fromISO(left.period.endDate || '');
          const rightEnd = DateTime.fromISO(right.period.endDate || '');

          const leftStartValue = leftStart.isValid ? leftStart.toMillis() : Number.MAX_SAFE_INTEGER;
          const rightStartValue = rightStart.isValid
            ? rightStart.toMillis()
            : Number.MAX_SAFE_INTEGER;
          if (leftStartValue !== rightStartValue) {
            return leftStartValue - rightStartValue;
          }

          const leftEndValue = leftEnd.isValid ? leftEnd.toMillis() : Number.MAX_SAFE_INTEGER;
          const rightEndValue = rightEnd.isValid ? rightEnd.toMillis() : Number.MAX_SAFE_INTEGER;
          if (leftEndValue !== rightEndValue) {
            return leftEndValue - rightEndValue;
          }

          const labelCompare = left.contractLabel.localeCompare(right.contractLabel);
          if (labelCompare !== 0) {
            return labelCompare;
          }

          if (left.sourceIndex !== right.sourceIndex) {
            return left.sourceIndex - right.sourceIndex;
          }

          return left.periodIndex - right.periodIndex;
        }),
    [billingSources, showSubcontracts]
  );

  const openCents = mergedBillingRows
    .filter(({ period }) => period.status !== 'paid')
    .reduce((sum, { period }) => sum + (period.amountCents || 0), 0);
  const overdueCents = mergedBillingRows
    .filter(({ period }) => isOverdueBillingPeriod(period))
    .reduce((sum, { period }) => sum + (period.amountCents || 0), 0);
  const planTotalCents = mergedBillingRows.reduce(
    (sum, { period }) => sum + (period.amountCents || 0),
    0
  );

  const statusCounts = mergedBillingRows.reduce(
    (acc, { period }) => {
      const status = (period.status || 'scheduled') as BillingPeriodStatus;
      acc[status] = (acc[status] || 0) + 1;
      if (isOverdueBillingPeriod(period)) acc.overdue += 1;
      return acc;
    },
    {
      scheduled: 0,
      billable: 0,
      invoiced: 0,
      paid: 0,
      overdue: 0,
    } as Record<BillingPeriodStatus, number> & { overdue: number }
  );

  const getBillingPeriods = (targetContractId: string) => {
    if (targetContractId === contractId) {
      return (form.getValues().billingPeriods || []) as ContractBillingPeriodFormData[];
    }
    return (
      subcontractSources.find((source) => source.contractId === targetContractId)?.billingPeriods ||
      []
    );
  };

  const getSourceByContractId = (targetContractId: string) => {
    return billingSources.find((source) => source.contractId === targetContractId);
  };

  const setBillingPeriods = (
    targetContractId: string,
    nextBillingPeriods: ContractBillingPeriodFormData[],
    opts?: { shouldDirty?: boolean }
  ) => {
    if (targetContractId === contractId) {
      form.setValue('billingPeriods', nextBillingPeriods as any, {
        shouldDirty: opts?.shouldDirty ?? true,
      });
      return;
    }

    onSubcontractBillingPeriodsChange?.(targetContractId, nextBillingPeriods);
  };

  const openBillingPeriodEditor = (targetContractId: string, periodIndex: number) => {
    if (!canEdit) return;
    setEditingBillingPeriodSnapshot(getBillingPeriods(targetContractId)[periodIndex] || null);
    setEditingTarget({ contractId: targetContractId, periodIndex });
  };

  const closeBillingPeriodEditor = (opts: { restore: boolean }) => {
    if (opts.restore && editingTarget && editingBillingPeriodSnapshot) {
      const current = getBillingPeriods(editingTarget.contractId);
      const next = current.map((period, index) =>
        index === editingTarget.periodIndex ? editingBillingPeriodSnapshot : period
      );
      setBillingPeriods(editingTarget.contractId, next, {
        shouldDirty: editingTarget.contractId !== contractId,
      });
    }
    setEditingTarget(null);
    setEditingBillingPeriodSnapshot(null);
  };

  const removeBillingPeriod = (targetContractId: string, periodIndex: number) => {
    if (!canEdit) return;
    const current = getBillingPeriods(targetContractId);
    const next = [...current.slice(0, periodIndex), ...current.slice(periodIndex + 1)];
    setBillingPeriods(targetContractId, next, { shouldDirty: true });
  };

  const addBillingPeriod = () => {
    if (!canEdit) return;
    const targetSource = getSourceByContractId(addTargetContractId);
    if (!targetSource) return;

    const due = targetSource.endDate
      ? DateTime.fromISO(targetSource.endDate)
          .plus({ days: Math.max(0, Math.trunc(targetSource.paymentTermsDays ?? 30)) })
          .toISO()
      : undefined;

    const next = [
      ...getBillingPeriods(targetSource.contractId),
      {
        startDate: targetSource.startDate || '',
        endDate: targetSource.endDate || '',
        amountCents: 0,
        status: 'scheduled' as BillingPeriodStatus,
        dueDate: due || undefined,
        invoiceNumber: '',
        note: '',
      },
    ];

    setBillingPeriods(targetSource.contractId, next, { shouldDirty: true });
  };

  const editingPeriod = editingTarget
    ? getBillingPeriods(editingTarget.contractId)[editingTarget.periodIndex]
    : null;
  const editingSource = editingTarget ? getSourceByContractId(editingTarget.contractId) : undefined;

  return (
    <div className="grid gap-6">
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-9">
          <DetailCard
            icon={Receipt}
            title={t('contracts.billingPlan')}
            headerAction={
              <div className="flex max-w-full flex-wrap items-center justify-end gap-2">
                {isAggregatedRootView ? (
                  <Toggle
                    size="sm"
                    variant="outline"
                    pressed={showSubcontracts}
                    onPressedChange={setShowSubcontracts}
                    className="h-8 shrink-0 text-xs"
                    data-testid="billing-toggle-subcontracts"
                  >
                    <Layers className="mr-2" size={14} />
                    {t('contracts.showSubcontracts')}
                  </Toggle>
                ) : null}

                {isAggregatedRootView && canEdit ? (
                  <div className="mx-1 h-4 w-px bg-border" />
                ) : null}

                {canEdit ? (
                  <>
                    {isAggregatedRootView ? (
                      <Select value={addTargetContractId} onValueChange={setAddTargetContractId}>
                        <SelectTrigger
                          className="h-8 w-[160px] shrink-0 py-1.5 text-xs xl:w-[180px]"
                          data-testid="billing-add-target-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {billingSources.map((source) => (
                            <SelectItem
                              key={source.contractId}
                              value={source.contractId}
                              className="text-xs"
                            >
                              {source.contractLabel}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 shrink-0 px-3 text-xs whitespace-nowrap"
                      data-testid="billing-add-period"
                      onClick={addBillingPeriod}
                    >
                      {t('contracts.addBillingPeriod')}
                    </Button>
                  </>
                ) : null}
              </div>
            }
            noPadding
          >
            <div className="px-6 py-3">
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="billing-status-summary"
              >
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('scheduled')}
                  data-testid="billing-status-chip-scheduled"
                >
                  {t('contracts.billingStatus.scheduled')} {statusCounts.scheduled}
                </Badge>
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('billable')}
                  data-testid="billing-status-chip-billable"
                >
                  {t('contracts.billingStatus.billable')} {statusCounts.billable}
                </Badge>
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('invoiced')}
                  data-testid="billing-status-chip-invoiced"
                >
                  {t('contracts.billingStatus.invoiced')} {statusCounts.invoiced}
                </Badge>
                <Badge
                  variant="outline-solid"
                  className={getStatusBadgeClass('paid')}
                  data-testid="billing-status-chip-paid"
                >
                  {t('contracts.billingStatus.paid')} {statusCounts.paid}
                </Badge>
                {statusCounts.overdue > 0 ? (
                  <Badge
                    variant="outline-solid"
                    className={getStatusBadgeClass('scheduled', { overdue: true })}
                    data-testid="billing-status-chip-overdue"
                  >
                    {t('contracts.billingStatus.overdue')} {statusCounts.overdue}
                  </Badge>
                ) : null}
              </div>
            </div>

            <Table data-testid="billing-plan-table">
              <TableHeader>
                <TableHeaderRow>
                  {isAggregatedRootView ? (
                    <TableHead className="w-[160px] pl-6 py-3 text-xs font-medium text-muted-foreground">
                      {t('contracts.billingPlanColumns.subcontract')}
                    </TableHead>
                  ) : null}
                  <TableHead
                    className={`${isAggregatedRootView ? '' : 'pl-6 '}w-[200px] py-3 text-xs font-medium text-muted-foreground`}
                  >
                    {t('contracts.billingPlanColumns.period')}
                  </TableHead>
                  <TableHead className="w-[120px] py-3 text-right text-xs font-medium text-muted-foreground">
                    {t('contracts.billingPlanColumns.amount')}
                  </TableHead>
                  <TableHead className="w-[130px] py-3 text-xs font-medium text-muted-foreground">
                    {t('contracts.billingPlanColumns.status')}
                  </TableHead>
                  <TableHead className="w-[140px] py-3 text-xs font-medium text-muted-foreground">
                    {t('contracts.billingPlanColumns.invoiceNumber')}
                  </TableHead>
                  <TableHead className="py-3 text-xs font-medium text-muted-foreground">
                    {t('contracts.billingPlanColumns.note')}
                  </TableHead>
                  {canEdit ? <TableHead className="w-[80px] pr-6 py-3" /> : null}
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {mergedBillingRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        canEdit ? (isAggregatedRootView ? 7 : 6) : isAggregatedRootView ? 6 : 5
                      }
                      className="pl-6 py-4 text-sm text-muted-foreground"
                    >
                      {t('contracts.billingPlanEmpty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  mergedBillingRows.map((row, index) => (
                    <TableRow
                      key={`${row.contractId}-${row.period.id ?? row.periodIndex}`}
                      data-testid={`billing-period-row-${index}`}
                      className={`group ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                      onClick={() => openBillingPeriodEditor(row.contractId, row.periodIndex)}
                    >
                      {isAggregatedRootView ? (
                        <TableCell className="pl-6 py-3">
                          <span
                            className="block max-w-[140px] truncate font-medium text-sm"
                            title={row.contractLabel}
                          >
                            {row.isRootContract ? t('contracts.rootContract') : row.contractLabel}
                          </span>
                        </TableCell>
                      ) : null}
                      <TableCell className={`${isAggregatedRootView ? '' : 'pl-6 '}py-3`}>
                        <div className="flex flex-col gap-0.5">
                          <div className="text-sm font-medium text-foreground whitespace-nowrap">
                            {(row.period.startDate
                              ? DateTime.fromISO(row.period.startDate).toFormat('dd.MM.yyyy')
                              : '-') +
                              ' - ' +
                              (row.period.endDate
                                ? DateTime.fromISO(row.period.endDate).toFormat('dd.MM.yyyy')
                                : '-')}
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">
                            {t('contracts.billingPlanColumns.dueDate')}:{' '}
                            {row.period.dueDate
                              ? DateTime.fromISO(row.period.dueDate).toFormat('dd.MM.yyyy')
                              : '-'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <span className="text-sm font-medium">
                          {formatCurrencyFromCents(Number(row.period.amountCents || 0), locale)}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge
                          variant="outline-solid"
                          className={getStatusBadgeClass(
                            (row.period.status || 'scheduled') as BillingPeriodStatus,
                            { overdue: isOverdueBillingPeriod(row.period) }
                          )}
                        >
                          {t(
                            `contracts.billingStatus.${
                              (row.period.status || 'scheduled') as BillingPeriodStatus
                            }` as any
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3">
                        {row.period.invoiceNumber ? (
                          <div
                            className="inline-flex max-w-[120px] truncate items-center rounded-md border border-border bg-muted/50 px-2 py-1 text-xs font-medium text-muted-foreground"
                            title={row.period.invoiceNumber}
                          >
                            {row.period.invoiceNumber}
                          </div>
                        ) : (
                          <span className="text-muted-foreground/50 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3">
                        {row.period.note ? (
                          <span
                            className="block max-w-[180px] truncate text-xs text-muted-foreground/80"
                            title={row.period.note}
                          >
                            {row.period.note}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/50 text-sm">-</span>
                        )}
                      </TableCell>
                      {canEdit ? (
                        <TableCell className="py-3 pr-6 text-right">
                          <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              data-testid={`billing-period-${index}-edit`}
                              onClick={(event) => {
                                event.stopPropagation();
                                openBillingPeriodEditor(row.contractId, row.periodIndex);
                              }}
                              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              type="button"
                              data-testid={`billing-period-${index}-remove`}
                              onClick={(event) => {
                                event.stopPropagation();
                                removeBillingPeriod(row.contractId, row.periodIndex);
                              }}
                              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <Dialog
              open={editingTarget !== null}
              onOpenChange={(open) => {
                if (!open) closeBillingPeriodEditor({ restore: true });
              }}
            >
              <DialogContent className="sm:max-w-[720px]" data-testid="billing-period-edit-dialog">
                {editingTarget && editingPeriod ? (
                  <div className="space-y-6">
                    <div className="space-y-1">
                      <div className="text-sm font-medium">{t('contracts.editBillingPeriod')}</div>
                      <div className="text-xs text-muted-foreground">
                        {isAggregatedRootView && editingSource
                          ? `${editingSource.contractLabel} · `
                          : ''}
                        {(editingPeriod.startDate
                          ? DateTime.fromISO(editingPeriod.startDate).toFormat('dd.MM.yyyy')
                          : '-') +
                          ' - ' +
                          (editingPeriod.endDate
                            ? DateTime.fromISO(editingPeriod.endDate).toFormat('dd.MM.yyyy')
                            : '-')}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormItem>
                        <FormLabel>{t('contracts.billingPlanColumns.startDate')}</FormLabel>
                        <FormControl>
                          <DatePicker
                            date={
                              editingPeriod.startDate
                                ? new Date(editingPeriod.startDate)
                                : undefined
                            }
                            disabled={!canEdit}
                            setDate={(date) => {
                              const next = [...getBillingPeriods(editingTarget.contractId)];
                              next[editingTarget.periodIndex] = {
                                ...next[editingTarget.periodIndex],
                                startDate: date?.toISOString() || '',
                              };
                              setBillingPeriods(editingTarget.contractId, next, {
                                shouldDirty: true,
                              });
                            }}
                            placeholder={t('contracts.billingPlanChooseDate')}
                          />
                        </FormControl>
                      </FormItem>
                      <FormItem>
                        <FormLabel>{t('contracts.billingPlanColumns.endDate')}</FormLabel>
                        <FormControl>
                          <DatePicker
                            date={
                              editingPeriod.endDate ? new Date(editingPeriod.endDate) : undefined
                            }
                            disabled={!canEdit}
                            setDate={(date) => {
                              const next = [...getBillingPeriods(editingTarget.contractId)];
                              next[editingTarget.periodIndex] = {
                                ...next[editingTarget.periodIndex],
                                endDate: date?.toISOString() || '',
                              };
                              setBillingPeriods(editingTarget.contractId, next, {
                                shouldDirty: true,
                              });
                            }}
                            placeholder={t('contracts.billingPlanChooseDate')}
                          />
                        </FormControl>
                      </FormItem>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormItem>
                        <FormLabel>{t('contracts.billingPlanColumns.dueDate')}</FormLabel>
                        <FormControl>
                          <DatePicker
                            date={
                              editingPeriod.dueDate ? new Date(editingPeriod.dueDate) : undefined
                            }
                            disabled={!canEdit}
                            setDate={(date) => {
                              const next = [...getBillingPeriods(editingTarget.contractId)];
                              next[editingTarget.periodIndex] = {
                                ...next[editingTarget.periodIndex],
                                dueDate: date?.toISOString() || undefined,
                              };
                              setBillingPeriods(editingTarget.contractId, next, {
                                shouldDirty: true,
                              });
                            }}
                            placeholder={t('contracts.billingPlanChooseDate')}
                          />
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>{t('contracts.billingPlanColumns.amount')}</FormLabel>
                        <FormControl>
                          <CurrencyInput
                            testId="billing-period-edit-amount"
                            valueCents={Number(editingPeriod.amountCents || 0)}
                            onValueCentsChange={(value) => {
                              const next = [...getBillingPeriods(editingTarget.contractId)];
                              next[editingTarget.periodIndex] = {
                                ...next[editingTarget.periodIndex],
                                amountCents: value,
                              };
                              setBillingPeriods(editingTarget.contractId, next, {
                                shouldDirty: true,
                              });
                            }}
                            disabled={!canEdit}
                          />
                        </FormControl>
                      </FormItem>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <FormItem>
                        <FormLabel>{t('contracts.billingPlanColumns.status')}</FormLabel>
                        <FormControl>
                          <Select
                            value={editingPeriod.status}
                            onValueChange={(value) => {
                              const next = [...getBillingPeriods(editingTarget.contractId)];
                              next[editingTarget.periodIndex] = {
                                ...next[editingTarget.periodIndex],
                                status: value as BillingPeriodStatus,
                              };
                              setBillingPeriods(editingTarget.contractId, next, {
                                shouldDirty: true,
                              });
                            }}
                            disabled={!canEdit}
                          >
                            <SelectTrigger data-testid="billing-period-edit-status">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="scheduled">
                                {t('contracts.billingStatus.scheduled')}
                              </SelectItem>
                              <SelectItem value="billable">
                                {t('contracts.billingStatus.billable')}
                              </SelectItem>
                              <SelectItem value="invoiced">
                                {t('contracts.billingStatus.invoiced')}
                              </SelectItem>
                              <SelectItem value="paid">
                                {t('contracts.billingStatus.paid')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </FormControl>
                      </FormItem>

                      <FormItem>
                        <FormLabel>{t('contracts.billingPlanColumns.invoiceNumber')}</FormLabel>
                        <FormControl>
                          <Input
                            data-testid="billing-period-edit-invoiceNumber"
                            value={editingPeriod.invoiceNumber ?? ''}
                            disabled={!canEdit}
                            onChange={(event) => {
                              const next = [...getBillingPeriods(editingTarget.contractId)];
                              next[editingTarget.periodIndex] = {
                                ...next[editingTarget.periodIndex],
                                invoiceNumber: event.target.value,
                              };
                              setBillingPeriods(editingTarget.contractId, next, {
                                shouldDirty: true,
                              });
                            }}
                          />
                        </FormControl>
                      </FormItem>
                    </div>

                    <FormItem>
                      <FormLabel>{t('contracts.billingPlanColumns.note')}</FormLabel>
                      <FormControl>
                        <Textarea
                          data-testid="billing-period-edit-note"
                          value={editingPeriod.note ?? ''}
                          disabled={!canEdit}
                          onChange={(event) => {
                            const next = [...getBillingPeriods(editingTarget.contractId)];
                            next[editingTarget.periodIndex] = {
                              ...next[editingTarget.periodIndex],
                              note: event.target.value,
                            };
                            setBillingPeriods(editingTarget.contractId, next, {
                              shouldDirty: true,
                            });
                          }}
                        />
                      </FormControl>
                    </FormItem>

                    <DialogFooter>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => closeBillingPeriodEditor({ restore: true })}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => closeBillingPeriodEditor({ restore: false })}
                        disabled={!canEdit}
                      >
                        {t('common.apply')}
                      </Button>
                    </DialogFooter>
                  </div>
                ) : null}
              </DialogContent>
            </Dialog>

            <div className="border-t border-border bg-muted/60 px-6 py-4">
              <div className="grid grid-cols-3 gap-3" data-testid="billing-amount-summary">
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="text-xs text-muted-foreground">
                    {t('contracts.billingSummary.total')}
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrencyFromCents(planTotalCents, locale)}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="text-xs text-muted-foreground">
                    {t('contracts.billingSummary.open')}
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrencyFromCents(openCents, locale)}
                  </div>
                </div>
                <div className="rounded-md border border-border bg-card p-3">
                  <div className="text-xs text-muted-foreground">
                    {t('contracts.billingSummary.overdue')}
                  </div>
                  <div className="text-sm font-medium">
                    {formatCurrencyFromCents(overdueCents, locale)}
                  </div>
                </div>
              </div>
            </div>
          </DetailCard>
        </div>

        <div className="space-y-6 lg:col-span-3">
          <DetailCard icon={Receipt} title={t('contracts.billingSettings')}>
            <p className="mb-6 text-sm text-muted-foreground">
              {t('contracts.billingSettingsDescription')}
            </p>
            {isAggregatedRootView ? (
              <p className="mb-6 text-xs text-muted-foreground">
                {t('contracts.subcontractBillingSettingsHint')}
              </p>
            ) : null}

            <div className="space-y-6">
              <FormField
                control={form.control}
                name="billingTotalCents"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingTotal')}</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        testId="billing-total-input"
                        valueCents={Number(field.value || 0)}
                        onValueCentsChange={field.onChange}
                        disabled={!canEdit}
                      />
                    </FormControl>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatCurrencyFromCents(Number(field.value || 0), locale)}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingInterval"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingInterval')}</FormLabel>
                    <FormControl>
                      <Select
                        value={normalizedBillingInterval}
                        onValueChange={(value) => {
                          form.setValue(
                            'billingInterval',
                            value as ContractFormData['billingInterval'],
                            {
                              shouldDirty: true,
                              shouldValidate: true,
                            }
                          );
                          field.onChange(value);
                        }}
                        disabled={!canEdit}
                      >
                        <SelectTrigger data-testid="billing-interval-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">
                            {t('contracts.billingIntervalOptions.monthly')}
                          </SelectItem>
                          <SelectItem value="quarterly">
                            {t('contracts.billingIntervalOptions.quarterly')}
                          </SelectItem>
                          <SelectItem value="yearly">
                            {t('contracts.billingIntervalOptions.yearly')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="paymentTermsDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.paymentTermsDays')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="payment-terms-input"
                        type="number"
                        value={String(field.value ?? 30)}
                        disabled={!canEdit}
                        onChange={(e) => field.onChange(Number.parseInt(e.target.value || '0', 10))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DetailCard>

          <DetailCard icon={User} title={t('contracts.billingContact')}>
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="billingPoNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingPoNumber')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="billing-po-input"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingContactName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingContactName')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="billing-contact-name-input"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingContactEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingContactEmail')}</FormLabel>
                    <FormControl>
                      <Input
                        data-testid="billing-contact-email-input"
                        type="email"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="billingAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contracts.billingAddress')}</FormLabel>
                    <FormControl>
                      <Textarea
                        data-testid="billing-address-input"
                        value={field.value ?? ''}
                        disabled={!canEdit}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </DetailCard>
        </div>
      </div>
    </div>
  );
}
